/**
 * TermsAcceptanceGate — global blocking modal that forces the authenticated
 * user to accept the current Standard License & Data-Usage Agreement before
 * they can use the app.
 *
 * Mounts at the App root. Shows whenever the backend reports
 * `termsAcceptanceRequired` (the user has never accepted, or accepted an
 * older version after we bumped it). This is the ONLY acceptance path for
 * Google OAuth users — there's no OAuth signup form to put a checkbox on —
 * and it also catches email/password users on a version bump.
 *
 * The user cannot dismiss it (no close button, backdrop click disabled,
 * Escape suppressed). The only way out is checking "I have read and agree"
 * and clicking accept, which calls `POST /api/auth/accept-terms`.
 *
 * Defers to `LegalNameRequiredModal` (also a blocking App-root gate): if the
 * profile is still missing a legal name, that modal goes first and this one
 * stays hidden until the name is filled.
 */

import { useEffect, useState } from 'react';
import { Loader2, ShieldCheck } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/state_management_and_stores/auth.store';
import { authService } from '@/services/auth.service';
import { useToast } from '@/contexts/ToastContext';
import { LegalAgreementBody, useLegalAgreement } from '@/components/legal/LegalAgreementDialog';

function isLegalNameMissing(
  profile: { first_name?: string | null; last_name?: string | null } | null | undefined,
): boolean {
  if (!profile) return false;
  return !(profile.first_name ?? '').trim() || !(profile.last_name ?? '').trim();
}

export function TermsAcceptanceGate() {
  const { isAuthenticated, profile, termsAcceptanceRequired, updateProfile, setTermsAccepted } =
    useAuthStore();

  // Show only when required AND the legal-name gate is satisfied — that modal
  // takes precedence so a brand-new OAuth user fills their name first.
  const open = isAuthenticated && termsAcceptanceRequired && !isLegalNameMissing(profile);

  // Only fetch the agreement text when we're actually going to show it.
  const { data: agreement, isLoading, isError } = useLegalAgreement(open);
  const { showError } = useToast();

  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Reset the checkbox each time the gate (re)opens.
  useEffect(() => {
    if (open) setAgreed(false);
  }, [open]);

  if (!open) return null;

  const handleAccept = async () => {
    if (!agreed || submitting) return;
    setSubmitting(true);
    try {
      const result = await authService.acceptTerms();
      if (result.profile) updateProfile(result.profile);
      setTermsAccepted();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to record your acceptance');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open}>
      <DialogContent
        hideClose
        closeOnOutsideClick={false}
        onEscapeKeyDown={(e) => e.preventDefault()}
        className="sm:max-w-2xl"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 text-primary">
            <ShieldCheck className="h-4 w-4" />
            <DialogTitle>{agreement?.title ?? 'Review and accept our agreement'}</DialogTitle>
          </div>
          <DialogDescription>
            Before you can continue using HighCall, please review and accept the Standard License
            &amp; Data-Usage Agreement.
          </DialogDescription>
        </DialogHeader>

        <LegalAgreementBody agreement={agreement} isLoading={isLoading} isError={isError} />

        <label className="flex items-start gap-2 pt-1 text-sm">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            disabled={submitting || isLoading || isError}
            className="mt-0.5 h-4 w-4 rounded border-input accent-primary"
          />
          <span>
            I have read and agree to the HighCall Standard License &amp; Data-Usage Agreement.
          </span>
        </label>

        <div className="flex justify-end pt-2">
          <Button type="button" onClick={handleAccept} disabled={!agreed || submitting || isLoading}>
            {submitting ? (
              <>
                <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                Saving…
              </>
            ) : (
              'Accept and continue'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
